// @givmo/board — what every board page shows before its own content (CBG-SEC2 SEC2-WEB).
//
// A board page is about the board its `?org=` names (`director/director-board.ts`). Before its own
// content it settles two things, in this order:
//   * the board is still being resolved → a skeleton (no read has gone out);
//   * the account holds no seat there → one quiet line and the way back to its boards.
// Otherwise, the page's own content. There is no board-level ask before it: every board has one rule,
// and a director reads on their account's sign-in (D-CBG-20; `director/read-gate.tsx`).
//
// The line says how the page got here (CBG-FIX34, frames f3 and f3b): a board `/me` listed earlier and
// no longer does — the board refused this account while the page was open, and `/me` was read again
// (`director/access-refusal.tsx`) — says the refusal's own approved sentence, "You no longer have
// access to this board."; an address naming a board this account never had a seat on keeps "You
// don’t have a seat on this board with this account.", whose "with this account" fits that case.
//
// CBG-FIX104 (rows 4.1 and 4.2, approved 2026-10-17): a seat `/me` listed as a committee's, for
// someone who isn't on the board, is told "You no longer have access to this committee." (or "these
// committees"); and the way back is home's own title (`directorHomeTitle`) — "Your committees" while
// every seat the account holds is a committee's, "Your boards and committees" for an account left
// holding none (review-233's F3), so the link names the page it opens. For an account home lands on
// one seat for (CBG-FIX109), that seat's page, straight there, in its title "Meetings" (`wayHome`).

import type { ReactNode } from 'react';
import { Skeleton, testIdProp } from '@givmo/design-system';
import { Link } from '../../board-routing';
import { directorHomeTitle, wayHome } from '../../director/director-board';
import { useDirectorMe, useListedSeat } from '../../director/director-me';
import { noLongerHasAccess } from '../../director/director-wire';

export function DirectorBoardContent({
  orgRef,
  notYours,
  children,
}: {
  orgRef: string | null;
  notYours: boolean;
  children: ReactNode;
}) {
  const listed = useListedSeat(orgRef);
  const meQuery = useDirectorMe();
  if (orgRef === null) {
    return <Skeleton className="h-84 w-full rounded-lg" testId="director-board-resolving" />;
  }
  if (notYours) {
    const home = wayHome(meQuery.data, directorHomeTitle(meQuery));
    return (
      <p
        className="font-sans text-s15 font-w400 leading-h15 tracking-givmo text-text-secondary"
        {...testIdProp(listed !== null ? 'director-board-lost' : 'director-board-not-yours')}
      >
        {listed !== null
          ? noLongerHasAccess(listed)
          : 'You don’t have a seat on this board with this account.'}{' '}
        <Link
          to={home.to}
          search={home.search}
          exact
          className="font-w600 text-brand-700 outline-none transition-colors hover:text-brand-800 focus-visible:underline"
        >
          {home.words}
        </Link>
      </p>
    );
  }
  return <>{children}</>;
}
